import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, CheckCircle2, LogIn, XCircle, AlertCircle, Repeat, User, ShoppingCart,
  Phone, MessageSquare, Mail, MessageCircle, Copy,
} from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import { isBlockingAppointmentStatus } from "@/lib/appointmentStatus";
import { CLINIC_TIME_ZONE, clinicDateAt, clinicIsoMinuteFromInstant, clinicTodayDateString } from "@/lib/clinicTime";
import {
  buildCalendarTimeSlots,
  buildDurationOptions,
  MIN_SLOT_INTERVAL_MINUTES,
  normalizeSlotIntervalMinutes,
} from "@/lib/calendarGranularity";
import { formatDoctorName } from "@/lib/doctorName";
import { checkInConfirmationMessage, shouldConfirmFutureCheckIn } from "@/lib/futureCheckInGuard";
import AppointmentOpsTimeline from "@/components/calendar/AppointmentOpsTimeline";

const TIME_CHIP_START_HOUR = 7;
const TIME_CHIP_END_HOUR = 20;

// =====================================================================
// AppointmentDetailPanel — refactor of the centered dialog into a
// SidePanel body. Read view + status transitions for one appointment.
// =====================================================================
export default function AppointmentDetailPanel({ appointment, doctors, chairs, settings = null, branchId = "", readOnly = false, tutorialDemo = false, onChanged, onCancel }) {
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [confirmationLink, setConfirmationLink] = useState(null);

  useEffect(() => {
    if (!appointment?.id) {
      setConfirmationLink(null);
      return undefined;
    }
    let cancelled = false;
    supabase.rpc("get_appointment_public_confirmation_link", {
      p_appointment_id: appointment.id,
    }).then(({ data }) => {
      if (cancelled) return;
      setConfirmationLink(data?.url_path ? data : null);
    }).catch(() => {
      if (!cancelled) setConfirmationLink(null);
    });
    return () => { cancelled = true; };
  }, [appointment?.id]);

  if (!appointment) return null;
  const a = appointment;
  const status = a.status;
  const isOpen = status === "Scheduled" || status === "Confirmed";
  const durationMinutes = minutesBetween(
    a.scheduled_start,
    a.scheduled_end,
    normalizeSlotIntervalMinutes(settings?.slot_interval_minutes),
  );

  async function call(rpc, args) {
    setBusy(true); setError(null);
    const { error: e } = await supabase.rpc(rpc, args);
    setBusy(false);
    if (e) { setError(e.message ?? "Could not update appointment."); return false; }
    return true;
  }

  async function handleConfirm() {
    if (await call("confirm_appointment", { p_appointment_id: a.id })) {
      toast.success("Appointment confirmed."); onChanged?.();
    }
  }
  async function handleCheckIn() {
    if (shouldConfirmFutureCheckIn(a) && !window.confirm(checkInConfirmationMessage(a))) {
      return;
    }
    if (tutorialDemo) {
      toast.success("Tutorial patient checked in.");
      onChanged?.();
      return;
    }
    if (await call("check_in_appointment", { p_appointment_id: a.id })) {
      toast.success("Patient checked in."); onChanged?.();
    }
  }
  async function handleNoShow() {
    if (!window.confirm("Mark as no-show? Any treatment needs on this visit will return to Open.")) return;
    if (await call("mark_appointment_no_show", { p_appointment_id: a.id })) {
      toast.success("Marked no-show."); onChanged?.();
    }
  }
  function openCancelDecision() {
    setRescheduleOpen(false);
    setCancelOpen(true);
    setError(null);
  }
  async function handleCancellationDecision(sendToRecall) {
    if (await call("cancel_appointment_with_recall_decision", {
      p_appointment_id: a.id,
      p_reason: cancelReason.trim() || null,
      p_send_to_recall: sendToRecall,
    })) {
      toast.success(sendToRecall
        ? "Appointment cancelled and sent to Recall."
        : "Appointment cancelled.");
      onChanged?.();
    }
  }

  async function copyConfirmationLink() {
    const url = buildPublicConfirmationUrl(confirmationLink?.url_path);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Confirmation link copied.");
    } catch {
      setError("Could not copy the confirmation link.");
    }
  }

  const noShowCount = Number(a.patient_no_show_count || 0);
  const hasContact = !!(a.patient_phone || a.patient_email);
  const smsBody = buildReminderSms(a);

  return (
    <div className="p-5 space-y-3 text-sm">
      <Row label="Patient">
        <div className="flex flex-wrap items-center gap-2">
          <Link to={`/patients/${a.patient_id}`} className="text-teal-700 font-semibold hover:underline inline-flex items-center gap-1"
            data-testid="appt-detail-patient-link">
            <User className="w-3.5 h-3.5" /> {a.patient_name || "—"}
          </Link>
          {noShowCount > 0 ? (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-rose-700"
              title={`${noShowCount} prior no-show${noShowCount === 1 ? "" : "s"} for this patient`}
              data-testid="appt-detail-noshow-badge"
            >
              <AlertCircle className="h-3 w-3" />
              {noShowCount} no-show{noShowCount === 1 ? "" : "s"}
            </span>
          ) : null}
        </div>
      </Row>
      <Row label="When">
        <span className="text-slate-800">{fmtFull(a.scheduled_start)} — {fmtHM(a.scheduled_end)}</span>
      </Row>
      <Row label="Duration">
        <div className="flex flex-wrap items-center gap-2" data-testid="appt-detail-duration">
          <span className="font-medium text-slate-800">{formatDurationOption(durationMinutes)}</span>
          {isOpen && !readOnly ? (
            <button
              type="button"
              onClick={() => setRescheduleOpen(true)}
              disabled={busy}
              data-testid="appt-change-duration"
              className="rounded border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-teal-700 hover:border-teal-300 hover:bg-teal-50 disabled:opacity-40"
            >
              Change duration
            </button>
          ) : null}
        </div>
      </Row>
      <Row label="Doctor"><span className="text-slate-700">{formatDoctorName(a.doctor_name, "Unassigned")}</span></Row>
      <Row label="Chair"><span className="text-slate-700">{a.chair_name || "—"}</span></Row>
      <Row label="Status">
        <span className="inline-flex items-center text-[11px] font-semibold tracking-wider uppercase rounded-full px-2 py-0.5 border"
          style={statusStyle(status)}>{status}</span>
      </Row>
      {hasContact ? (
        <Row label="Contact">
          <div className="flex flex-wrap items-center gap-1.5" data-testid="appt-detail-contact-actions">
            {a.patient_phone ? (
              <a
                href={`tel:${a.patient_phone}`}
                className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                data-testid="appt-detail-call"
                title={`Call ${a.patient_phone}`}
              >
                <Phone className="h-3 w-3" /> Call
              </a>
            ) : null}
            {a.patient_phone ? (
              <a
                href={`sms:${a.patient_phone}?&body=${encodeURIComponent(smsBody)}`}
                className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                data-testid="appt-detail-sms"
                title="Send reminder text"
              >
                <MessageSquare className="h-3 w-3" /> SMS
              </a>
            ) : null}
            {a.patient_phone ? (
              <a
                href={`https://wa.me/${normalizePhoneForWhatsApp(a.patient_phone)}?text=${encodeURIComponent(smsBody)}`}
                target="_blank"
                rel="noreferrer noopener"
                className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                data-testid="appt-detail-whatsapp"
                title="Open WhatsApp"
              >
                <MessageCircle className="h-3 w-3" /> WhatsApp
              </a>
            ) : null}
            {a.patient_email ? (
              <a
                href={`mailto:${a.patient_email}?subject=${encodeURIComponent("Appointment reminder")}&body=${encodeURIComponent(smsBody)}`}
                className="inline-flex items-center gap-1 rounded border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
                data-testid="appt-detail-email"
                title={`Email ${a.patient_email}`}
              >
                <Mail className="h-3 w-3" /> Email
              </a>
            ) : null}
          </div>
        </Row>
      ) : null}
      {a.notes ? (
        <Row label="Notes"><span className="text-slate-700 whitespace-pre-wrap">{a.notes}</span></Row>
      ) : null}

      {error ? (
        <div role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>
      ) : null}

      {readOnly && (isOpen || status === "Checked In") ? (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid="appt-read-only-lockout"
        >
          Billing read-only mode: appointment changes and checkout are blocked, but the schedule stays visible.
        </div>
      ) : null}

      {isOpen && !readOnly ? (
        <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
          {status === "Scheduled" ? (
            <button onClick={handleConfirm} disabled={busy} data-testid="appt-confirm"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-violet-100 text-violet-900 rounded hover:bg-violet-200 disabled:opacity-40">
              <CheckCircle2 className="w-3.5 h-3.5" /> Confirm
            </button>
          ) : null}
          <button onClick={handleCheckIn} disabled={busy} data-testid="appt-checkin" data-tutorial-id="appt-checkin"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-emerald-100 text-emerald-900 rounded hover:bg-emerald-200 disabled:opacity-40">
            <LogIn className="w-3.5 h-3.5" /> Check in
          </button>
          <button onClick={() => setRescheduleOpen(true)} disabled={busy} data-testid="appt-reschedule"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-amber-100 text-amber-900 rounded hover:bg-amber-200 disabled:opacity-40">
            <Repeat className="w-3.5 h-3.5" /> Reschedule
          </button>
          <button onClick={handleNoShow} disabled={busy} data-testid="appt-noshow"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-rose-100 text-rose-900 rounded hover:bg-rose-200 disabled:opacity-40">
            <AlertCircle className="w-3.5 h-3.5" /> No-show
          </button>
          <button onClick={openCancelDecision} disabled={busy} data-testid="appt-cancel"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-slate-200 text-slate-600 rounded hover:bg-slate-50 disabled:opacity-40 ml-auto">
            <XCircle className="w-3.5 h-3.5" /> Cancel
          </button>
        </div>
      ) : null}

      {status === "Checked In" && !readOnly ? (
        <div className="pt-3 border-t border-slate-100 flex flex-wrap items-center gap-2">
          <Link to={`/checkout?appointment_id=${a.id}`} data-testid="appt-checkout"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold bg-teal-600 text-white rounded hover:bg-teal-700">
            <ShoppingCart className="w-3.5 h-3.5" /> Start checkout
          </Link>
          {/* Intentionally no cancellation action after check-in. A patient
              who arrived must proceed through checkout, not be cancelled. */}
        </div>
      ) : null}

      {cancelOpen ? (
        <section
          className="space-y-3 rounded-lg border border-rose-200 bg-rose-50/60 p-3"
          aria-labelledby="cancel-appointment-title"
          data-testid="appt-cancel-decision"
        >
          <div>
            <h3 id="cancel-appointment-title" className="text-sm font-semibold text-slate-900">Cancel appointment</h3>
            <p className="mt-1 text-xs leading-5 text-slate-600">
              Send to Recall cancels this slot and returns its linked treatment to the active Recall queue now. Cancel only releases the slot. Linked treatment remains open and follows normal Recall rules.
            </p>
          </div>
          <div>
            <Label>Reason (optional)</Label>
            <textarea
              value={cancelReason}
              onChange={(event) => setCancelReason(event.target.value)}
              rows={2}
              disabled={busy}
              data-testid="appt-cancel-reason"
              className="w-full resize-y rounded border border-slate-200 bg-white px-2.5 py-2 text-sm text-slate-800 outline-none placeholder:text-slate-400 focus:border-rose-400 disabled:opacity-60"
              placeholder="For example, patient requested a later date"
            />
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => handleCancellationDecision(true)}
              disabled={busy}
              data-testid="appt-cancel-send-recall"
              className="inline-flex items-center gap-1.5 rounded bg-teal-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-teal-800 disabled:opacity-40"
            >
              <Repeat className="h-3.5 w-3.5" /> Cancel and send to Recall
            </button>
            <button
              type="button"
              onClick={() => handleCancellationDecision(false)}
              disabled={busy}
              data-testid="appt-cancel-only"
              className="inline-flex items-center gap-1.5 rounded border border-rose-300 bg-white px-3 py-1.5 text-xs font-semibold text-rose-800 hover:bg-rose-100 disabled:opacity-40"
            >
              <XCircle className="h-3.5 w-3.5" /> Cancel only
            </button>
            <button
              type="button"
              onClick={() => { setCancelOpen(false); setError(null); }}
              disabled={busy}
              data-testid="appt-cancel-back"
              className="px-1 py-1.5 text-xs font-semibold text-slate-600 hover:text-slate-900 disabled:opacity-40"
            >
              Keep appointment
            </button>
          </div>
        </section>
      ) : null}

      {rescheduleOpen ? (
        <ReschedulePanel
          appointment={a} doctors={doctors} chairs={chairs}
          settings={settings}
          branchId={branchId}
          onClose={() => setRescheduleOpen(false)}
          onDone={() => { setRescheduleOpen(false); onChanged?.(); }}
        />
      ) : null}

      {confirmationLink?.url_path ? (
        <div className="border-t border-slate-100 pt-3" data-testid="appt-confirmation-link-section">
          <p className="text-[11px] font-semibold tracking-wider uppercase text-slate-400">Patient confirmation link</p>
          <div className="mt-1 flex items-center gap-2">
            <p className="min-w-0 flex-1 break-all rounded bg-slate-50 px-2 py-1.5 font-mono text-[11px] text-slate-500">
              {buildPublicConfirmationUrl(confirmationLink.url_path)}
            </p>
            <button
              type="button"
              onClick={copyConfirmationLink}
              data-testid="appt-confirmation-link-copy"
              className="inline-flex h-8 w-8 items-center justify-center rounded border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              title="Copy patient confirmation link"
            >
              <Copy className="h-3.5 w-3.5" />
              <span className="sr-only">Copy patient confirmation link</span>
            </button>
          </div>
        </div>
      ) : null}

      <AppointmentOpsTimeline appointmentId={a.id} />
    </div>
  );
}

function ReschedulePanel({ appointment, doctors, chairs, settings, branchId = "", onClose, onDone }) {
  const slotIntervalMinutes = normalizeSlotIntervalMinutes(settings?.slot_interval_minutes);
  const appointmentDurationMin = useMemo(
    () => minutesBetween(appointment.scheduled_start, appointment.scheduled_end, slotIntervalMinutes),
    [appointment.scheduled_start, appointment.scheduled_end, slotIntervalMinutes],
  );
  const [start, setStart] = useState(toLocalIsoMinute(appointment.scheduled_start));
  const [durationMin, setDurationMin] = useState(appointmentDurationMin);
  const [doctorId, setDoctorId] = useState(appointment.doctor_id || "");
  const [chairId,  setChairId]  = useState(appointment.chair_id  || "");
  const [reason,   setReason]   = useState("");
  const [busy, setBusy]   = useState(false);
  const [error, setError] = useState(null);
  const [dayAppts, setDayAppts] = useState([]);
  const [dayLoading, setDayLoading] = useState(false);
  const durationOptions = useMemo(
    () => durationOptionsFor(slotIntervalMinutes, appointmentDurationMin, durationMin),
    [appointmentDurationMin, durationMin, slotIntervalMinutes],
  );
  const timeChipSlots = useMemo(
    () => buildTimeChipSlots(slotIntervalMinutes),
    [slotIntervalMinutes],
  );
  const selectedDate = splitLocalIsoMinute(start).date;
  const selectedTime = splitLocalIsoMinute(start).time;

  useEffect(() => {
    if (!selectedDate || !branchId) {
      setDayAppts([]);
      setDayLoading(false);
      return undefined;
    }
    let cancelled = false;
    setDayLoading(true);
    const from = clinicDateAt(selectedDate, 0, 0, 0).toISOString();
    const to = clinicDateAt(selectedDate, 23, 59, 59).toISOString();
    const branchAppointments = supabase.rpc("list_appointments", {
      p_from: from,
      p_to: to,
      p_doctor_id: null,
      p_chair_id: null,
      p_branch_id: branchId,
    });
    const doctorBlocks = doctorId
      ? supabase.rpc("list_doctor_availability_blocks", {
        p_from: from,
        p_to: to,
        p_doctor_id: doctorId,
        p_branch_id: branchId,
        p_exclude_appointment_id: appointment.id,
      })
      : Promise.resolve({ data: [], error: null });
    Promise.all([branchAppointments, doctorBlocks]).then(([branchRes, doctorRes]) => {
      if (cancelled) return;
      setDayAppts(mergeBlockingAvailabilityRows(
        branchRes?.data ?? [],
        doctorRes?.error ? [] : (doctorRes?.data ?? []),
        appointment.id,
      ));
    }).catch(() => {
      if (!cancelled) setDayAppts([]);
    }).finally(() => {
      if (!cancelled) setDayLoading(false);
    });
    return () => { cancelled = true; };
  }, [appointment.id, branchId, doctorId, selectedDate]);

  const slotAvailability = useMemo(() => {
    const map = {};
    if (!selectedDate) return map;
    for (const slot of timeChipSlots) {
      map[slot] = rescheduleSlotState({
        date: selectedDate,
        time: slot,
        durationMin,
        doctorId,
        chairId,
        dayAppts,
      });
    }
    return map;
  }, [selectedDate, durationMin, doctorId, chairId, dayAppts, timeChipSlots]);

  async function go() {
    if (!start) { setError("Start time is required."); return; }
    if (!durationMin || durationMin <= 0) { setError("Duration is required."); return; }
    const selectedState = slotAvailability[selectedTime] || "free";
    if (rescheduleSlotUnavailable(selectedState)) {
      setError(rescheduleSlotLabel(selectedState) || "This slot is unavailable.");
      return;
    }
    setBusy(true); setError(null);
    const { date, time } = splitLocalIsoMinute(start);
    const [hour = "0", minute = "0"] = time.split(":");
    const nextStart = clinicDateAt(date, Number(hour), Number(minute));
    const nextEnd = new Date(nextStart.getTime() + durationMin * 60_000);
    const { error: e } = await supabase.rpc("reschedule_appointment_in_place", {
      p_appointment_id: appointment.id,
      p_new_start:      nextStart.toISOString(),
      p_new_end:        nextEnd.toISOString(),
      p_new_doctor_id:  doctorId || null,
      p_new_chair_id:   chairId  || null,
      p_request_id:     null,
      p_patient_message: reason || null,
    });
    setBusy(false);
    if (e) { setError(e.message ?? "Could not reschedule."); return; }
    toast.success("Appointment rescheduled.");
    onDone?.();
  }

  return (
    <div className="mt-3 -mx-5 -mb-3 space-y-4 border-t border-slate-100 bg-slate-50/70 px-5 py-4">
      <div>
        <p className="text-sm font-semibold text-slate-900">Reschedule</p>
        <p className="mt-0.5 text-xs text-slate-500">Change the date, time, duration, doctor, or chair.</p>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label>Date</Label>
          <input
            id="rs-date"
            type="date"
            value={selectedDate}
            onChange={(e) => { setStart(joinLocalIsoMinute(e.target.value, selectedTime || "09:00")); setError(null); }}
            data-testid="rs-date"
            className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-teal-500"
          />
        </div>
        <div>
          <Label>Chair</Label>
          <select
            id="rs-chair"
            value={chairId}
            onChange={(e) => { setChairId(e.target.value); setError(null); }}
            data-testid="rs-chair"
            className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded"
          >
            <option value="">No chair</option>
            {chairs.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between gap-2">
          <Label>Time</Label>
          {dayLoading ? <Loader2 className="mb-1 h-3.5 w-3.5 animate-spin text-slate-400" /> : null}
        </div>
        <TimeChipGrid
          value={selectedTime}
          slots={timeChipSlots}
          availability={slotAvailability}
          onChange={(t) => { setStart(joinLocalIsoMinute(selectedDate || todayDateStr(), t)); setError(null); }}
          testPrefix="rs"
        />
      </div>

      <div>
        <Label>Duration</Label>
        <select
          value={durationMin}
          onChange={(e) => { setDurationMin(Number(e.target.value)); setError(null); }}
          data-testid="rs-duration"
          className="w-full sm:max-w-[12rem] px-2.5 py-1.5 text-sm border border-slate-200 rounded bg-white"
        >
          {durationOptions.map((m) => (
            <option key={m} value={m}>{formatDurationOption(m)}</option>
          ))}
        </select>
      </div>

      <div>
        <Label>Doctor</Label>
        <select
          id="rs-doctor"
          value={doctorId}
          onChange={(e) => { setDoctorId(e.target.value); setError(null); }}
          data-testid="rs-doctor"
          className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded"
        >
          <option value="">Unassigned</option>
          {doctors.map((d) => <option key={d.id} value={d.id}>{d.display_name || "Unnamed"}</option>)}
        </select>
      </div>

      <div>
        <Label>Reason (optional)</Label>
        <input type="text" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. patient requested"
          data-testid="rs-reason"
          className="w-full px-2.5 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:border-teal-500" />
      </div>

      {error ? (
        <div role="alert" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>
      ) : null}

      <div className="flex items-center justify-end gap-2 border-t border-slate-100 pt-3">
        <button onClick={onClose} disabled={busy}
          className="px-3 py-1.5 text-xs border border-slate-200 rounded text-slate-600 hover:bg-slate-50">Back</button>
        <button onClick={go} disabled={busy} data-testid="rs-confirm"
          className="px-4 py-1.5 text-xs font-semibold bg-teal-600 text-white rounded hover:bg-teal-700 disabled:opacity-40 inline-flex items-center gap-1.5">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Reschedule
        </button>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[80px_1fr] gap-3 items-start">
      <span className="text-[11px] font-semibold tracking-wider uppercase text-slate-400 mt-0.5">{label}</span>
      <div>{children}</div>
    </div>
  );
}
function Label({ children }) {
  return (
    <label className="block text-xs font-semibold text-slate-700 mb-1">{children}</label>
  );
}
function statusStyle(s) {
  switch (normalizeStatus(s)) {
    case "Scheduled":   return { background: "#e0f2fe", color: "#075985", borderColor: "#bae6fd" };
    case "Confirmed":   return { background: "#d1fae5", color: "#065f46", borderColor: "#a7f3d0" };
    case "Checked In":  return { background: "#fef3c7", color: "#92400e", borderColor: "#fde68a" };
    case "Completed":   return { background: "#e2e8f0", color: "#334155", borderColor: "#cbd5e1" };
    case "No Show":     return { background: "#ffe4e6", color: "#881337", borderColor: "#fecdd3" };
    default:            return { background: "#f1f5f9", color: "#475569", borderColor: "#cbd5e1" };
  }
}
function normalizeStatus(status) {
  if (status === "CheckedIn") return "Checked In";
  if (status === "NoShow") return "No Show";
  return status || "Scheduled";
}
function fmtFull(ts) { if (!ts) return "—"; return new Date(ts).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short", timeZone: CLINIC_TIME_ZONE }); }
function fmtHM(ts)   { if (!ts) return ""; return new Date(ts).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: CLINIC_TIME_ZONE }); }

function buildReminderSms(a) {
  const name = (a?.patient_name || "").split(" ")[0] || "there";
  const when = a?.scheduled_start
    ? new Date(a.scheduled_start).toLocaleString(undefined, { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit", timeZone: CLINIC_TIME_ZONE })
    : "your scheduled time";
  return `Hi ${name}! Reminder of your dental appointment on ${when}. Please reply YES to confirm or call us to reschedule. Thank you!`;
}

function normalizePhoneForWhatsApp(phone) {
  if (!phone) return "";
  // Strip all non-digit characters; keep leading + if present is fine because wa.me wants digits only.
  const digits = String(phone).replace(/[^\d]/g, "");
  if (digits.startsWith("0") && digits.length === 11) {
    // PH local format 09xx-xxx-xxxx → 639xxxxxxxxx
    return `63${digits.slice(1)}`;
  }
  return digits;
}
function buildPublicConfirmationUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const origin = window.location?.origin || "https://dental-os.app";
  return `${origin}${path.startsWith("/") ? path : `/${path}`}`;
}
function toLocalIsoMinute(isoOrDate) {
  return clinicIsoMinuteFromInstant(isoOrDate);
}
function minutesBetween(start, end, fallback = MIN_SLOT_INTERVAL_MINUTES) {
  const a = new Date(start);
  const b = new Date(end);
  const fallbackMin = normalizeSlotIntervalMinutes(fallback);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return fallbackMin;
  return Math.max(fallbackMin, Math.round((b.getTime() - a.getTime()) / 60_000));
}
function durationOptionsFor(slotIntervalMinutes, ...values) {
  return buildDurationOptions(slotIntervalMinutes, values);
}
function formatDurationOption(minutes) {
  const n = Number(minutes) || 0;
  if (n < 60) return `${n} min`;
  const h = Math.floor(n / 60);
  const m = n % 60;
  return m ? `${h} hr ${m} min` : `${h} hr`;
}
function splitLocalIsoMinute(iso) {
  if (!iso || typeof iso !== "string") return { date: "", time: "" };
  const [date = "", time = ""] = iso.split("T");
  return { date, time: time.slice(0, 5) };
}
function joinLocalIsoMinute(date, time) {
  if (!date) return "";
  return `${date}T${time || "09:00"}`;
}
function todayDateStr() {
  return clinicTodayDateString();
}
function buildTimeChipSlots(slotIntervalMinutes) {
  return buildCalendarTimeSlots({
    startHour: TIME_CHIP_START_HOUR,
    endHour: TIME_CHIP_END_HOUR,
    slotIntervalMinutes,
  });
}
function TimeChipGrid({ value, slots = [], availability = {}, onChange, testPrefix }) {
  return (
    <div className="grid grid-cols-4 gap-1.5 max-h-40 overflow-y-auto rounded-lg border border-slate-100 bg-white p-1.5">
      {slots.map((slot) => {
        const selected = value === slot;
        const state = availability[slot] || "free";
        const unavailable = rescheduleSlotUnavailable(state);
        const label = rescheduleSlotLabel(state);
        return (
          <button
            key={slot}
            type="button"
            disabled={unavailable}
            onClick={() => onChange(slot)}
            data-testid={`${testPrefix}-time-${slot}`}
            title={label || undefined}
            className={[
              "rounded-md px-2 py-1.5 text-xs font-medium tabular-nums",
              unavailable
                ? "cursor-not-allowed border border-slate-200 bg-slate-100 text-slate-400 line-through"
                : selected
                ? "bg-teal-600 text-white"
                : "bg-slate-50 text-slate-700 hover:bg-slate-100",
            ].join(" ")}
          >
            {formatChipTime(slot)}
            {label ? <span className="sr-only"> {label}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
function rescheduleSlotState({ date, time, durationMin, doctorId, chairId, dayAppts }) {
  const [hour = "0", minute = "0"] = String(time || "").split(":");
  const start = clinicDateAt(date, Number(hour), Number(minute));
  const end = new Date(start.getTime() + Number(durationMin || 0) * 60_000);
  for (const a of dayAppts || []) {
    const aStart = new Date(a.scheduled_start);
    const aEnd = new Date(a.scheduled_end);
    if (Number.isNaN(aStart.getTime()) || Number.isNaN(aEnd.getTime())) continue;
    if (start < aEnd && end > aStart) {
      if (doctorId && a.doctor_id === doctorId) return "doctor_busy";
      if (chairId && a.chair_id === chairId) return "chair_busy";
      if (!doctorId && !chairId) return "busy";
    }
  }
  return "free";
}
function mergeBlockingAvailabilityRows(branchRows = [], doctorRows = [], excludeAppointmentId = null) {
  const rowsById = new Map();
  for (const row of [...branchRows, ...doctorRows]) {
    if (!row || row.id === excludeAppointmentId || !isBlockingAppointmentStatus(row.status)) continue;
    rowsById.set(row.id || `${row.doctor_id || ""}:${row.scheduled_start}:${row.scheduled_end}`, row);
  }
  return [...rowsById.values()];
}
function rescheduleSlotUnavailable(state) {
  return state !== "free";
}
function rescheduleSlotLabel(state) {
  if (state === "doctor_busy") return "Doctor Unavailable";
  if (state === "chair_busy") return "Chair Unavailable";
  if (state === "busy") return "Unavailable";
  return null;
}
function formatChipTime(time) {
  const [hh, mm] = String(time || "").split(":").map(Number);
  if (!Number.isFinite(hh)) return time;
  const suffix = hh >= 12 ? "PM" : "AM";
  const hour = hh % 12 || 12;
  return `${hour}:${String(mm || 0).padStart(2, "0")} ${suffix}`;
}
